/**
 * Price-match handoff.
 *
 * A successful match is confirmed inside the assistant, but the payoff belongs
 * on the product page — which may not be mounted yet, because the same turn can
 * navigate there. So the match is written to sessionStorage and announced on a
 * window event: a page already open reacts to the event, a page that mounts
 * afterwards picks the record up.
 *
 * Two separate questions are answered here, and they are deliberately not the
 * same one:
 *   getPriceMatch(id)                what price to show for this product now
 *   takePriceMatchCelebration(id)    whether to play the celebration, once
 */

const KEY = "orbiee_price_match";
export const PRICE_MATCH_EVENT = "orbiee:price-matched";

/** A match older than this is still honoured on screen, but never re-celebrated. */
const CELEBRATION_TTL_MS = 90_000;

const readAll = () => {
  try {
    const raw = sessionStorage.getItem(KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
  } catch {
    // Private mode, blocked storage, or corrupt JSON — the feature degrades to
    // "no match recorded", never to a crash.
    return {};
  }
};

const writeAll = (map) => {
  try {
    sessionStorage.setItem(KEY, JSON.stringify(map));
  } catch {
    /* ignore */
  }
};

const toId = (v) => (v != null && String(v).trim() !== "" ? String(v).trim() : null);

const toPrice = (v) => {
  if (v == null || v === "") return null;
  const n = typeof v === "number" ? v : parseFloat(String(v).replace(/[^0-9.-]/g, ""));
  return Number.isFinite(n) && n > 0 ? n : null;
};

/**
 * Did this payload report a successful match?
 *
 * One definition, used by the turn assembler and by the flow, because the
 * wording varies by deployment: the current backend answers
 * `status: "PRICE_MATCHED"`, others say "matched", "success" or "applied".
 * Accept any status that says it matched and does not also say it failed,
 * rather than a whitelist that has to be kept in step with the agent.
 *
 * @param {unknown} payload a tool result, or an action shaped from one
 */
export function isPriceMatchSuccess(payload) {
  if (!payload || typeof payload !== "object") return false;
  const p = /** @type {Record<string, unknown>} */ (payload);

  if (p.error != null && p.error !== "") return false;
  if (p.matched === true || p.success === true) return true;

  const status = String(p.status ?? p.result ?? "").toLowerCase();
  const price = p.matched_price ?? p.new_price ?? p.final_price ?? p.found_price;
  if (!status) return price != null;

  const negative =
    /(not|no_|non|fail|reject|deni|error|unavailable|pending|missing|ineligible)/.test(
      status
    );
  return !negative && /match|success|approved|applied|ok/.test(status);
}

/** `expires_at` as epoch ms, or null when absent or unparseable. */
const toExpiry = (v) => {
  if (v == null || v === "") return null;
  if (typeof v === "number" && Number.isFinite(v)) {
    // Seconds or milliseconds — anything below this threshold is seconds.
    return v < 1e12 ? v * 1000 : v;
  }
  const t = Date.parse(String(v));
  return Number.isFinite(t) ? t : null;
};

const isExpired = (entry) =>
  entry?.expiresAt != null && Date.now() > Number(entry.expiresAt);

/**
 * Record a successful price match and announce it.
 *
 * @param {{ productId: unknown, matchedPrice: unknown, originalPrice?: unknown,
 *           currency?: unknown, competitor?: unknown, expiresAt?: unknown }} input
 * @returns {object | null} the stored entry, or null when the payload is unusable
 */
export function recordPriceMatch(input) {
  const productId = toId(input?.productId);
  const matchedPrice = toPrice(input?.matchedPrice);
  if (!productId || matchedPrice == null) return null;

  const originalPrice = toPrice(input?.originalPrice);
  const entry = {
    productId,
    matchedPrice,
    // Only a higher "was" price is worth keeping: a match that did not drop the
    // price has nothing to celebrate and must not render a fake strike-through.
    originalPrice: originalPrice != null && originalPrice > matchedPrice ? originalPrice : null,
    currency: typeof input?.currency === "string" && input.currency ? input.currency : "USD",
    competitor: typeof input?.competitor === "string" ? input.competitor.trim() : "",
    // The offer window the backend quoted. Past it, the shelf price is the
    // truth again and the page must stop showing the matched one.
    expiresAt: toExpiry(input?.expiresAt),
    at: Date.now(),
    celebrated: false,
  };

  const map = readAll();
  map[productId] = entry;
  writeAll(map);

  if (process.env.NODE_ENV !== "production") {
    // One line to answer "did the turn reach the page?" when it does not fire.
    console.debug("[PriceMatch] recorded", entry);
  }

  try {
    window.dispatchEvent(new CustomEvent(PRICE_MATCH_EVENT, { detail: entry }));
  } catch {
    /* CustomEvent unsupported — a page mounting later still reads the record */
  }
  return entry;
}

/** The match on record for a product, if any. */
export function getPriceMatch(productId) {
  const id = toId(productId);
  if (!id) return null;
  const entry = readAll()[id];
  if (!entry || toPrice(entry.matchedPrice) == null || isExpired(entry)) return null;
  return entry;
}

/**
 * Claim the celebration for a product. Returns the entry the first time only,
 * and marks it consumed **before** returning — a double-invoked effect (React
 * StrictMode) or a remount must never replay the animation.
 */
export function takePriceMatchCelebration(productId) {
  const id = toId(productId);
  if (!id) return null;

  const map = readAll();
  const entry = map[id];
  if (!entry || entry.celebrated) return null;

  entry.celebrated = true;
  writeAll(map);

  if (isExpired(entry)) return null;
  if (Date.now() - (Number(entry.at) || 0) > CELEBRATION_TTL_MS) return null;
  return entry;
}

/** The agent saying it matched — not the agent saying it will go and look. */
const CONFIRMED =
  /\b(we\s+can\s+match|able\s+to\s+match|happy\s+to\s+match|(?:we|i)(?:'|’)?ve\s+matched|(?:we|i)\s+(?:have\s+)?matched|price[-\s]?matched|matched\s+(?:the|that|this)\s+price|matched\s+price)\b/i;
/** …and the outcome actually stated, which a "let me check" preamble never is. */
const SETTLED =
  /\b(it(?:'|’)?ll\s+be|it\s+will\s+be|your\s+(?:new\s+)?price|new\s+price|price\s+will\s+be|now\s+costs?|applied\s+automatically|at\s+checkout)\b/i;
/** A refusal uses the same vocabulary, so it has to be excluded explicitly. */
const DENIED =
  /\b(can(?:'|’)?t|cannot|can\s+not|unable|not\s+able|couldn(?:'|’)?t|won(?:'|’)?t|isn(?:'|’)?t\s+eligible|not\s+eligible|unfortunately|sorry|no\s+longer)\b/i;
/** Only amounts carrying a currency — "24 hours" must never read as a price. */
const MONEY = /(?:USD|US\$|\$|₹|€|£|INR|EUR|GBP)\s?([0-9][\d,]*(?:\.\d{1,2})?)/gi;

/**
 * Pull a confirmed match out of assistant prose.
 *
 * Deliberately strict: an explicit confirmation, a stated outcome, no refusal
 * wording, and at least one currency amount. The product page ignores a match
 * that does not beat the price it is showing, so a misread stays invisible.
 *
 * @param {unknown} rawText
 * @returns {{ matchedPrice: number, originalPrice: number | null } | null}
 */
export function parsePriceMatchFromText(rawText) {
  const text = typeof rawText === "string" ? rawText : "";
  if (!text || !CONFIRMED.test(text) || !SETTLED.test(text) || DENIED.test(text)) {
    return null;
  }

  const amounts = [];
  let m;
  MONEY.lastIndex = 0;
  while ((m = MONEY.exec(text)) !== null) {
    const n = parseFloat(m[1].replace(/,/g, ""));
    if (Number.isFinite(n) && n > 0) amounts.push(n);
  }
  if (!amounts.length) return null;

  return {
    // Where the reply quotes both prices, the match is the lower one.
    matchedPrice: Math.min(...amounts),
    originalPrice: amounts.length > 1 ? Math.max(...amounts) : null,
  };
}

/**
 * The product the shopper is looking at, read from the URL.
 *
 * Last-resort fallback for a match whose payload names no product and whose
 * screen context is unavailable: on a detail route, the product on screen is
 * the one the conversation is about.
 */
export function productIdFromLocation() {
  try {
    const m = String(window.location?.pathname || "").match(
      /\/(?:item|product)\/([^/?#]+)/
    );
    return m ? decodeURIComponent(m[1]) : null;
  } catch {
    return null;
  }
}

/** Listen for matches recorded during this session. Returns an unsubscribe. */
export function subscribePriceMatch(callback) {
  if (typeof callback !== "function") return () => {};
  const handler = (e) => callback(e?.detail || null);
  window.addEventListener(PRICE_MATCH_EVENT, handler);
  return () => window.removeEventListener(PRICE_MATCH_EVENT, handler);
}
